import type { Components } from "react-markdown";

import { AssistantResponseMarkdownView } from "@product/components/app/runs/assistant-response-markdown-view";
import {
  WORKSPACE_LINK_CONTENT_CLASS,
  isBareLink,
  plainLabel,
} from "@product/components/content/link-display";
import { MarkdownImage } from "@product/components/content/markdown-image";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { staticMarkdownRendererComponents } from "@product/components/content/markdown-renderer-components-static";
import { workspaceLink } from "@product/components/content/workspace-link";
import { WorkspaceLinkView } from "@product/components/content/workspace-link-view";

/**
 * The renderer map an assistant turn uses, for the links and images a shot's
 * answers contain: the shared static map, the product's image component for
 * attached files, and BrandLink's workspace branch for record links. Written
 * without hooks so the whole answer renders on the server.
 *
 * A record link is BrandLink's workspace chip: the author's words as its
 * title and no detail beside them. A bare link would show the link's own
 * label, as the chip does before the workspace store has the record's name.
 * While the answer streams, the wipe wraps the link's words in spans, and
 * BrandLink then leaves the link as it is until they settle; so does this.
 */
export const SHOT_MARKDOWN_COMPONENTS: Partial<Components> = {
  ...staticMarkdownRendererComponents,
  // `node` is the hast element react-markdown passes every renderer; the
  // image component never reads it, so it stays out of the client payload.
  img: ({ node: _node, ...props }) => <MarkdownImage {...props} />,
  a: ({ node: _node, href, children }) => {
    const workspace = href ? workspaceLink(href, "") : null;
    if (!href || !workspace) {
      throw new Error(
        `Product shot answers link only to workspace records; got ${href ?? "no href"}.`,
      );
    }
    const label = isBareLink(href, children) ? undefined : plainLabel(children);
    if (label === null) {
      return (
        <a href={href} data-workspace-link={workspace.kind} className={WORKSPACE_LINK_CONTENT_CLASS}>
          {children}
        </a>
      );
    }
    return (
      <WorkspaceLinkView href={href} kind={workspace.kind} title={label ?? workspace.label} detail={null} />
    );
  },
};

/** Markdown as an assistant turn renders it: the product's normalisation,
 *  typeset wrapper and plugins, with the shot's renderer map. */
export function ShotMarkdown({
  children,
  className,
  streaming = false,
  rehypePlugins,
}: {
  children: string;
  className?: string;
  streaming?: boolean;
  rehypePlugins?: Parameters<typeof AssistantResponseMarkdownView>[0]["rehypePlugins"];
}) {
  return (
    <AssistantResponseMarkdownView
      className={className}
      streaming={streaming}
      components={SHOT_MARKDOWN_COMPONENTS}
      rehypePlugins={rehypePlugins}
    >
      {normalizeMarkdownContent(children, { streaming })}
    </AssistantResponseMarkdownView>
  );
}
